import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import {
  getMaterials,
  getWatchlist,
  addToWatchlist,
  removeFromWatchlist,
  seedData,
  seedTranslations,
  getSustainability,
  getListings,
} from '@/lib/api';

import { OnboardingWizard, useOnboarding } from '@/components/onboarding/OnboardingWizard';
import { PostWantedAdDialog } from '@/components/wanted/PostWantedAdDialog';
import { RecentWantedAdsStrip } from '@/components/wanted/RecentWantedAdsStrip';

import { HeroSection } from '@/components/home/sections/HeroSection';
import { SustainabilityBanner } from '@/components/home/sections/SustainabilityBanner';
import { MaterialGridSection } from '@/components/home/sections/MaterialGridSection';
import { SparePartTeaserSection } from '@/components/home/sections/SparePartTeaserSection';
import { PaintTeaserSection } from '@/components/home/sections/PaintTeaserSection';
import { MachineTeaserSection } from '@/components/home/sections/MachineTeaserSection';
import { NewCompanyCTASection } from '@/components/home/sections/NewCompanyCTASection';
import { TrustSignalsSection } from '@/components/home/sections/TrustSignalsSection';

export const HomePage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showOnboarding, dismissOnboarding] = useOnboarding(user);

  // Material grid state
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [sortBy, setSortByState] = useState(() => {
    if (typeof window === 'undefined') return 'created_at';
    return window.localStorage.getItem('materials.sortBy') || 'created_at';
  });
  const setSortBy = (val) => {
    setSortByState(val);
    try { window.localStorage.setItem('materials.sortBy', val); } catch (e) { /* ignore */ }
  };
  const [sortOrder, setSortOrderState] = useState(() => {
    if (typeof window === 'undefined') return 'desc';
    const v = window.localStorage.getItem('materials.sortOrder');
    return v === 'asc' || v === 'desc' ? v : 'desc';
  });
  const setSortOrder = (val) => {
    const next = typeof val === 'function' ? val(sortOrder) : val;
    setSortOrderState(next);
    try { window.localStorage.setItem('materials.sortOrder', next); } catch (e) { /* ignore */ }
  };
  const [viewMode, setViewModeState] = useState(() => {
    if (typeof window === 'undefined') return 'grid';
    const stored = window.localStorage.getItem('materials.viewMode');
    return stored === 'list' || stored === 'grid' ? stored : 'grid';
  });
  const setViewMode = (mode) => {
    setViewModeState(mode);
    try { window.localStorage.setItem('materials.viewMode', mode); } catch (e) { /* ignore */ }
  };
  const [seeding, setSeeding] = useState(false);
  const [widthMin, setWidthMin] = useState('');
  const [widthMax, setWidthMax] = useState('');

  // Vertical teasers + sustainability
  const [sustainability, setSustainability] = useState(null);
  const [spareParts, setSpareParts] = useState([]);
  const [machines, setMachines] = useState([]);
  const [paints, setPaints] = useState([]);
  const [verticalCountsLoading, setVerticalCountsLoading] = useState(true);

  // Hero search + wanted ads
  const [heroQuery, setHeroQuery] = useState('');
  const [heroVertical, setHeroVertical] = useState('spare_parts');
  const [wantedAdOpen, setWantedAdOpen] = useState(false);
  const [recentAdsRefresh, setRecentAdsRefresh] = useState(0);

  useEffect(() => {
    loadMaterials();
    if (user) loadWatchlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, sortBy, sortOrder, user, widthMin, widthMax]);

  useEffect(() => {
    getSustainability()
      .then((res) => setSustainability(res.data))
      .catch((err) => console.error('Failed to load sustainability:', err));
    loadVerticalTeasers();
  }, []);

  const loadVerticalTeasers = async () => {
    setVerticalCountsLoading(true);
    try {
      const [spareRes, machineRes, paintsRes] = await Promise.all([
        getListings({ vertical: 'spare_parts', limit: 4, status: 'active' }),
        getListings({ vertical: 'machines', limit: 3, status: 'active' }),
        getListings({ vertical: 'paints', limit: 3, status: 'active' }),
      ]);
      setSpareParts(spareRes.data?.items || []);
      setMachines(machineRes.data?.items || []);
      setPaints(paintsRes.data?.items || []);
    } catch (err) {
      console.error('Failed to load vertical teasers', err);
    } finally {
      setVerticalCountsLoading(false);
    }
  };

  const loadMaterials = async () => {
    setLoading(true);
    try {
      const params = {
        category_id: selectedCategory,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: 20,
      };
      if (widthMin) params.min_width = parseFloat(widthMin);
      if (widthMax) params.max_width = parseFloat(widthMax);
      const response = await getMaterials(params);
      setMaterials(response.data);
    } catch (error) {
      console.error('Failed to load materials:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWatchlist = async () => {
    try {
      const response = await getWatchlist();
      setWatchlist(response.data.map((item) => item.material_id));
    } catch (error) {
      console.error('Failed to load watchlist:', error);
    }
  };

  const handleWatchlistToggle = async (materialId) => {
    if (!user) {
      toast.error(t('home.loginRequired'));
      return;
    }
    try {
      if (watchlist.includes(materialId)) {
        await removeFromWatchlist(materialId);
        setWatchlist((prev) => prev.filter((id) => id !== materialId));
        toast.success(t('material.removeFromWatchlist'));
      } else {
        await addToWatchlist(materialId);
        setWatchlist((prev) => [...prev, materialId]);
        toast.success(t('material.addToWatchlist'));
      }
    } catch (error) {
      console.error('Watchlist error:', error);
      toast.error(t('home.watchlistError'));
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await seedData();
      await seedTranslations();
      toast.success(t('home.seedSuccess'));
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Seed error:', error);
      toast.error(error.response?.data?.detail || t('home.seedError'));
    } finally {
      setSeeding(false);
    }
  };

  const handleHeroSearch = (e) => {
    e.preventDefault();
    const q = heroQuery.trim();
    if (heroVertical === 'materials') {
      navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    } else {
      const qs = new URLSearchParams({ vertical: heroVertical });
      if (q) qs.set('q', q);
      navigate(`/listings?${qs.toString()}`);
    }
  };

  const heroCO2 = sustainability?.estimated_co2_saved_kg || 0;
  const heroCO2Display = heroCO2 >= 1000
    ? { value: heroCO2 / 1000, decimals: 1, unit: ' t' }
    : { value: heroCO2, decimals: 0, unit: ' kg' };

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-slate-950 flex flex-col" data-testid="home-page">
      {showOnboarding && <OnboardingWizard onClose={dismissOnboarding} />}
      <Header />

      <HeroSection
        sustainability={sustainability}
        heroCO2Display={heroCO2Display}
        heroQuery={heroQuery}
        setHeroQuery={setHeroQuery}
        heroVertical={heroVertical}
        setHeroVertical={setHeroVertical}
        onHeroSearch={handleHeroSearch}
        onPostWantedAd={() => setWantedAdOpen(true)}
        machines={machines}
        spareParts={spareParts}
        paints={paints}
        verticalCountsLoading={verticalCountsLoading}
      />

      <SustainabilityBanner sustainability={sustainability} heroCO2Display={heroCO2Display} />

      <MaterialGridSection
        materials={materials}
        loading={loading}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        viewMode={viewMode}
        setViewMode={setViewMode}
        widthMin={widthMin}
        setWidthMin={setWidthMin}
        widthMax={widthMax}
        setWidthMax={setWidthMax}
        watchlist={watchlist}
        onWatchlistToggle={handleWatchlistToggle}
        onSeedData={handleSeedData}
        seeding={seeding}
      />

      <SparePartTeaserSection spareParts={spareParts} loading={verticalCountsLoading} />

      <PaintTeaserSection paints={paints} loading={verticalCountsLoading} />

      <section className="bg-white border-b border-slate-200" data-testid="wanted-ads-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <RecentWantedAdsStrip
            key={recentAdsRefresh}
            onPostClick={() => (user ? setWantedAdOpen(true) : navigate('/login?next=/wanted'))}
          />
        </div>
      </section>

      <MachineTeaserSection machines={machines} loading={verticalCountsLoading} />

      <NewCompanyCTASection />

      <TrustSignalsSection />

      <Footer />
      <PostWantedAdDialog
        open={wantedAdOpen}
        onClose={() => setWantedAdOpen(false)}
        onCreated={() => setRecentAdsRefresh((n) => n + 1)}
      />
    </div>
  );
};
